import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import { TrustBadges } from "@/components/common/TrustBadges";
import { ApplyNowModal } from "@/components/common/ApplyNowModal";
import { Home, User, Banknote, Car, Calculator as CalculatorIcon, CreditCard, GraduationCap, Coins, Check } from "lucide-react";
import { toast } from "sonner";

interface LoanProduct {
  icon: any;
  title: string;
  description: string;
  minAmount: string;
  maxAmount: string;
  rate: string;
  tenure: string;
  features: string[];
  specialOffers: string[];
  eligibility: string[];
}

interface ComparisonLoan extends Omit<LoanProduct, 'minAmount' | 'maxAmount' | 'rate' | 'tenure'> {
  amount: number;
  rate: number;
  tenure: number;
  emi?: number;
  totalInterest?: number;
  totalPayment?: number;
}

const LoanProducts = () => {
  const navigate = useNavigate();
  const [selectedLoans, setSelectedLoans] = useState<number[]>([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [comparisonLoans, setComparisonLoans] = useState<ComparisonLoan[]>([]);

  // Calculate EMI function
  const calculateEMI = (amount: number, rate: number, tenure: number) => {
    const monthlyRate = rate / 12 / 100;
    const emi = (amount * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
                (Math.pow(1 + monthlyRate, tenure) - 1);
    return emi;
  };

  const handleCalculateEMI = (loanType: string) => {
    navigate('/calculators', {
      state: {
        activeTab: 'personal',
        activeCalculator: `${loanType} EMI`
      }
    });
  };

  const toggleLoanSelection = (index: number) => {
    setSelectedLoans(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else if (prev.length < 4) {
        return [...prev, index];
      } else {
        toast.warning('You can compare up to 4 loans at a time');
        return prev;
      }
    });
  };

  const handleCompareLoans = () => {
    if (selectedLoans.length < 2) {
      toast.warning('Please select at least 2 loans to compare');
      return;
    }

    // Initialize comparison loans with default values
    const loans = selectedLoans.map(index => {
      const product = loanProducts[index];
      const amount = parseFloat(product.minAmount.replace(/[^0-9.]/g, ''));
      const rate = parseFloat(product.rate);
      const tenure = parseInt(product.tenure.split('-')[0].trim());
      const emi = calculateEMI(amount, rate, tenure);

      return {
        ...product,
        amount,
        rate,
        tenure,
        emi,
        totalInterest: emi * tenure - amount,
        totalPayment: emi * tenure
      };
    });

    setComparisonLoans(loans);
    setIsCompareOpen(true);
  };

  const handleUpdateLoan = (index: number, field: 'amount' | 'rate' | 'tenure', value: number) => {
    setComparisonLoans(prev => {
      const updated = [...prev];
      const loan = { ...updated[index] };

      // Update the field
      loan[field] = value;

      // Recalculate EMI and totals
      loan.emi = calculateEMI(loan.amount, loan.rate, loan.tenure);
      loan.totalInterest = loan.emi * loan.tenure - loan.amount;
      loan.totalPayment = loan.emi * loan.tenure;

      updated[index] = loan;
      return updated;
    });
  };

  const handleDownloadComparison = () => {
    if (comparisonLoans.length === 0) return;

    let comparisonText = '🌟 LOAN COMPARISON 🌟\n\n';

    // Add loan details
    comparisonText += 'Loan Details:\n';
    comparisonText += '='.repeat(80) + '\n';

    comparisonLoans.forEach((loan, index) => {
      comparisonText += `\n${loan.title.toUpperCase()}\n`;
      comparisonText += '─'.repeat(40) + '\n';
      comparisonText += `Loan Amount: ₹${loan.amount.toLocaleString()}\n`;
      comparisonText += `Interest Rate: ${loan.rate}% p.a.\n`;
      comparisonText += `Tenure: ${loan.tenure} months\n`;
      comparisonText += `EMI: ₹${loan.emi?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\n`;
      comparisonText += `Total Interest: ₹${loan.totalInterest?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\n`;
      comparisonText += `Total Payment: ₹${loan.totalPayment?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}\n`;

      if (index < comparisonLoans.length - 1) {
        comparisonText += '\n' + '─'.repeat(80) + '\n';
      }
    });

    // Create and trigger download
    const blob = new Blob([comparisonText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `loan-comparison-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Comparison downloaded successfully!');
  };

  const getComparisonData = () => {
    return selectedLoans.map(index => loanProducts[index]);
  };
  const loanProducts = [
    {
      icon: Home,
      title: "Home Loan",
      description: "Make your dream home a reality",
      minAmount: "₹1,00,000",
      maxAmount: "₹5,00,00,000",
      rate: "8.40%",
      tenure: "5-30 years",
      features: ["Up to 90% Funding", "Balance Transfer", "Top-up Available", "Tax Benefits"],
      specialOffers: ["Zero Processing Fee", "Free Valuation", "No Prepayment Penalty"],
      eligibility: ["Age: 23-62 years", "Income: ₹25,000+ per month", "Property documents"]
    },
    {
      icon: Banknote,
      title: "Business Loan",
      description: "Fuel your business growth",
      minAmount: "₹50,000",
      maxAmount: "₹50,00,000",
      rate: "12.00%",
      tenure: "12-84 months",
      features: ["Working Capital", "Equipment Financing", "Quick Disbursal", "Flexible Repayment"],
      specialOffers: ["48-hour Approval", "Collateral-free options", "GST Benefits"],
      eligibility: ["Business vintage: 2+ years", "Annual turnover: ₹10L+", "ITR filed"]
    },
    {
      icon: CalculatorIcon,
      title: "Mortgage Loan",
      description: "Loans against property",
      minAmount: "₹5,00,000",
      maxAmount: "₹10,00,00,000",
      rate: "9.75%",
      tenure: "5-20 years",
      features: ["Up to 70% of Property Value", "Multiple End-use", "Balance Transfer", "Part-payment"],
      specialOffers: ["Lowest Rates", "Quick Processing", "Flexible Tenure"],
      eligibility: ["Property owner", "Age: 25-65 years", "Income proof"]
    },
    {
      icon: Home,
      title: "Project Loan",
      description: "Finance your property development project",
      minAmount: "₹10,00,000",
      maxAmount: "₹50,00,00,000",
      rate: "10.25%",
      tenure: "5-15 years",
      features: ["Up to 75% of Project Cost", "Flexible Repayment", "Technical Support", "Multiple Disbursements"],
      specialOffers: ["Competitive Rates", "Customized Solutions", "Expert Guidance"],
      eligibility: ["Project Feasibility Report", "Clear Title Deeds", "25% Margin Money"]
    },
    {
      icon: User,
      title: "Personal Loan",
      description: "Instant personal loans for any purpose",
      minAmount: "₹10,000",
      maxAmount: "₹20,00,000",
      rate: "10.49%",
      tenure: "12-60 months",
      features: ["No Collateral Required", "Instant Approval", "Flexible EMI", "Minimal Documentation"],
      specialOffers: ["Zero Processing Fee", "Special Rates for Salaried"],
      eligibility: ["Age: 21-60 years", "Income: ₹15,000+ per month", "CIBIL: 650+"]
    },
    {
      icon: GraduationCap,
      title: "Education Loan",
      description: "Invest in your future",
      minAmount: "₹50,000",
      maxAmount: "₹1,50,00,000",
      rate: "9.15%",
      tenure: "5-15 years",
      features: ["Covers 100% Fees", "Moratorium Period", "Tax Benefits", "Abroad Studies"],
      specialOffers: ["No Collateral up to ₹7.5L", "Interest subsidy", "Merit scholarships"],
      eligibility: ["Admission confirmed", "Co-applicant required", "Academic records"]
    },
    {
      icon: Car,
      title: "New Car Loan",
      description: "Finance your brand new car",
      minAmount: "₹1,00,000",
      maxAmount: "₹1,50,00,000",
      rate: "8.25%",
      tenure: "12-84 months",
      features: ["100% On-Road Funding", "Quick Approval", "Flexible EMI", "Free First Year Insurance"],
      specialOffers: ["Zero Down Payment", "Free RSA", "Exchange Bonus"],
      eligibility: ["Age: 21-65 years", "Income: ₹25,000+ per month", "Valid License"]
    },
    {
      icon: Car,
      title: "Used Car Loan",
      description: "Pre-owned car financing",
      minAmount: "₹50,000",
      maxAmount: "₹75,00,000",
      rate: "9.50%",
      tenure: "12-60 months",
      features: ["Up to 90% of Car Value", "Quick Processing", "Minimal Documentation", "Flexible Tenure"],
      specialOffers: ["Low Interest Rates", "No Hidden Charges", "Quick Disbursal"],
      eligibility: ["Car Age: Up to 5 years", "Income: ₹20,000+ per month", "Valid RC & Insurance"]
    },
    {
      icon: CreditCard,
      title: "Credit Card",
      description: "Lifetime free credit cards",
      minAmount: "₹25,000",
      maxAmount: "₹10,00,000",
      rate: "0% intro",
      tenure: "Lifetime",
      features: ["Lifetime Free", "Reward Points", "Cashback", "Travel Benefits"],
      specialOffers: ["₹5000 Welcome Bonus", "Airport Lounge Access", "Movie Tickets"],
      eligibility: ["Age: 21-60 years", "Income: ₹20,000+ per month", "CIBIL: 700+"]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-20">
      {/* Hero Section */}
      <section className="pt-8 pb-4 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
           Services
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Choose from our comprehensive range of service products designed to meet every financial need
          </p>
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <Button
              size="lg"
              onClick={handleCompareLoans}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8 py-4 h-auto"
              disabled={selectedLoans.length === 0}
            >
              Compare Selected ({selectedLoans.length})
            </Button>
            {selectedLoans.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setSelectedLoans([])}
                className="text-white border-white/30 hover:bg-white/10"
              >
                Clear Selection
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="pt-0 px-4 pb-16">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {loanProducts.map((product, index) => (
              <Card key={index} className="relative bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300">
                <div className="absolute top-4 right-4">
                  <Checkbox
                    id={`select-${index}`}
                    checked={selectedLoans.includes(index)}
                    onCheckedChange={() => toggleLoanSelection(index)}
                    className="h-5 w-5 rounded border-white/50 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                  />
                  <Label htmlFor={`select-${index}`} className="sr-only">
                    Select for comparison
                  </Label>
                </div>
                <CardHeader>
                  <div className="flex items-center mb-4">
                    <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mr-4">
                      <product.icon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl text-white">{product.title}</CardTitle>
                      <CardDescription className="text-gray-300 text-lg">
                        {product.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Loan Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-500/20 p-3 rounded-lg text-center">
                      <div className="text-blue-400 font-semibold text-sm">Amount Range</div>
                      <div className="text-white font-bold">{product.minAmount} - {product.maxAmount}</div>
                    </div>
                    <div className="bg-green-500/20 p-3 rounded-lg text-center">
                      <div className="text-green-400 font-semibold text-sm">Interest Rate</div>
                      <div className="text-white font-bold">{product.rate}* p.a.</div>
                    </div>
                  </div>

                  <div className="bg-purple-500/20 p-3 rounded-lg text-center">
                    <div className="text-purple-400 font-semibold text-sm">Tenure</div>
                    <div className="text-white font-bold">{product.tenure}</div>
                  </div>

                  {/* Features */}
                  <div>
                    <h4 className="text-white font-semibold mb-2">Key Features:</h4>
                    <div className="grid grid-cols-2 gap-1">
                      {product.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-gray-300 text-sm">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></div>
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Special Offers */}
                  <div>
                    <h4 className="text-white font-semibold mb-2">Special Offers:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.specialOffers.map((offer, idx) => (
                        <Badge key={idx} className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                          {offer}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Eligibility */}
                  <div>
                    <h4 className="text-white font-semibold mb-2">Basic Eligibility:</h4>
                    <div className="space-y-1">
                      {product.eligibility.map((criteria, idx) => (
                        <div key={idx} className="text-gray-300 text-sm">
                          • {criteria}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3 pt-4">
                    <ApplyNowModal loanType={product.title}>
                      <Button
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                      >
                        Apply Now
                      </Button>
                    </ApplyNowModal>
                    <Button
                      variant="outline"
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                      onClick={() => handleCalculateEMI(product.title)}
                    >
                      Calculate EMI
                    </Button>
                  </div>

                  <TrustBadges />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <FloatingButtons />

      {/* Comparison Modal */}
      <Dialog open={isCompareOpen} onOpenChange={setIsCompareOpen}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-lg border-blue-500/30 text-white max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-center bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Compare Loan Products
            </DialogTitle>
          </DialogHeader>

          <div className="overflow-x-auto">
            <Table className="border-collapse">
              <TableHeader>
                <TableRow className="border-b border-white/20">
                  <TableHead className="w-[200px] text-white font-semibold">Features</TableHead>
                  {getComparisonData().map((product, idx) => (
                    <TableHead key={idx} className="text-center text-white font-semibold min-w-[250px]">
                      <div className="flex flex-col items-center">
                        <product.icon className="h-10 w-10 mb-2 text-blue-400" />
                        <span className="text-lg">{product.title}</span>
                        <span className="text-sm text-gray-300">{product.description}</span>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="border-b border-white/10">
                  <TableCell className="font-medium">Interest Rate</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx} className="text-center">
                      <span className="text-xl font-bold text-blue-400">{product.rate}</span> p.a.
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="border-b border-white/10">
                  <TableCell className="font-medium">Loan Amount</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx} className="text-center">
                      {product.minAmount} - {product.maxAmount}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="border-b border-white/10">
                  <TableCell className="font-medium">Tenure</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx} className="text-center">
                      {product.tenure}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="border-b border-white/10">
                  <TableCell className="font-medium">Key Features</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx}>
                      <ul className="space-y-2">
                        {product.features.map((feature, fIdx) => (
                          <li key={fIdx} className="flex items-start">
                            <Check className="h-4 w-4 text-green-400 mt-1 mr-2 flex-shrink-0" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow className="border-b border-white/10">
                  <TableCell className="font-medium">Special Offers</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx}>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {product.specialOffers.map((offer, oIdx) => (
                          <Badge key={oIdx} variant="outline" className="text-xs py-1 px-2 border-blue-400/30 text-blue-300 bg-blue-500/10">
                            {offer}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Eligibility</TableCell>
                  {getComparisonData().map((product, idx) => (
                    <TableCell key={idx}>
                      <ul className="space-y-1">
                        {product.eligibility.map((item, eIdx) => (
                          <li key={eIdx} className="text-sm flex items-start">
                            <span className="text-blue-400 mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {comparisonLoans.map((loan, index) => (
              <div key={index} className="bg-slate-800/50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold mb-3 text-blue-300">{loan.title}</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm text-gray-300">Loan Amount (₹)</Label>
                    <Input
                      type="number"
                      value={loan.amount}
                      onChange={(e) => handleUpdateLoan(index, 'amount', parseFloat(e.target.value) || 0)}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-300">Interest Rate (% p.a.)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={loan.rate}
                      onChange={(e) => handleUpdateLoan(index, 'rate', parseFloat(e.target.value) || 0)}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-300">Tenure (months)</Label>
                    <Input
                      type="number"
                      value={loan.tenure}
                      onChange={(e) => handleUpdateLoan(index, 'tenure', parseInt(e.target.value) || 0)}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  <div className="pt-2 border-t border-slate-700">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">Monthly EMI:</span>
                      <span className="font-medium text-blue-300">₹{loan.emi?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-300">Total Interest:</span>
                      <span className="text-amber-400">₹{loan.totalInterest?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-gray-300">Total Payment:</span>
                      <span className="text-green-400">₹{loan.totalPayment?.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-slate-700 mt-6">
            <div className="text-sm text-gray-400">
              Adjust the values and compare different loan scenarios
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={handleDownloadComparison}
                className="bg-blue-500/10 border-blue-500/30 text-blue-300 hover:bg-blue-500/20 hover:text-white"
              >
                Download Comparison
              </Button>
              <DialogClose asChild>
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  Close
                </Button>
              </DialogClose>
              <ApplyNowModal>
                <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                  Apply Now
                </Button>
              </ApplyNowModal>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoanProducts;
